<template>
  <div class="relative space-y-6 lg:space-y-8 px-3 sm:px-4 lg:px-8 min-h-[calc(100vh-8rem)] pt-6">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div>
        <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary dark:text-secondary">
          My Materials
        </h1>
        <p class="text-sm text-slate-500 dark:text-slate-200 mt-2">
          View assigned materials, online forms, and documents.
        </p>
      </div>
    </div>

    <TableControls
      v-model:search="searchQuery"
      v-model:status="statusFilter"
      v-model:perPage="perPage"
      :options="statusOptions"
      filterLabel="Status"
      placeholder="Search materials..."
      class="md:sticky md:top-[80px]"
    />

    <!-- Type Filter -->
    <div class="flex gap-2 -mt-2 mb-4 overflow-x-auto pb-2">
      <button v-for="type in typeOptions" :key="type.value"
        @click="typeFilter = type.value"
        class="px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap"
        :class="typeFilter === type.value ? 'bg-primary text-white shadow-sm' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'">
        {{ type.label }}
      </button>
    </div>

    <div v-if="error" class="bg-red-50 text-red-600 p-4 rounded-xl border border-red-200">
      {{ error }}
    </div>

    <div v-else-if="loading" class="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200/50 dark:border-slate-700/50 overflow-hidden">
      <table class="min-w-full">
        <tbody>
          <TableLoader :colspan="1" title="Fetching Materials" />
        </tbody>
      </table>
    </div>

    <div v-else-if="paginatedMaterials.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      <div
        v-for="m in paginatedMaterials"
        :key="m.id"
        class="relative bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-5 flex flex-col overflow-hidden"
      >
        <!-- Completed Ribbon -->
        <div v-if="isCompleted(m)" class="absolute top-0 right-0 w-24 h-24 overflow-hidden pointer-events-none z-0">
          <div class="absolute top-[23px] right-[-44px] rotate-45 bg-emerald-600 text-white text-[10px] font-bold uppercase px-10 py-1 shadow-md tracking-wider">Completed</div>
        </div>

        <!-- Top: Type Badge -->
        <div class="flex items-center gap-2 mb-3">
			<span
				:class="[
					'inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg',
					m.type === 'Doc File'
					? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
					: m.type === 'Online form'
					? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
					: m.type === 'URL Link'
					? 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400'
					: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
				]"
			>
				<FileText v-if="m.type === 'Doc File'" class="h-3.5 w-3.5 text-amber-700" />
				<Globe v-else-if="m.type === 'Online form'" class="h-3.5 w-3.5 text-blue-700" />
				<Link2 v-else-if="m.type === 'URL Link'" class="h-3.5 w-3.5 text-purple-700" />
				{{ m.type }}
			</span>
			<span v-if="m.type === 'Doc File'" class="group relative inline-flex">
				<Info class="h-3.5 w-3.5 text-yellow-600 cursor-help" />
				<span class="absolute top-full left-1/2 -translate-x-1/2 mt-2 hidden group-hover:block w-52 bg-slate-800 text-white text-xs rounded px-2 py-1 shadow-lg whitespace-normal text-center z-[9999]">
					Download the document, fill it out, then click Submit to upload your completed file.
					<span class="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-slate-800"></span>
				</span>
			</span>
        </div>

        <!-- Title & Description -->
        <h3 class="font-semibold text-base text-slate-800 dark:text-slate-100 mb-1.5 leading-snug">{{ m.title }}</h3>
        <p class="text-sm text-slate-500 dark:text-slate-400 flex-1 leading-relaxed">
          {{ m.description || 'No description provided.' }}
        </p>

        <!-- Footer: Actions -->
        <div class="flex items-center justify-end mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
          <!-- Online form: completed -->
          <button
            v-if="m.type === 'Online form' && isCompleted(m)"
            disabled
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg cursor-not-allowed"
          >
            <CheckCircle class="h-3.5 w-3.5 shrink-0" />
            Completed
          </button>

          <!-- View Details button (all types) -->
          <button
            v-else-if="(m.type === 'Online form' || m.file_path || m.type === 'URL Link') && !isCompleted(m)"
            @click="handleView(m)"
            class="group inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-blue-600 hover:bg-blue-600 hover:text-white transition-all"
            title="View Details"
          >
            <ViewIcon class="h-4 w-4 shrink-0" />
            <span class="overflow-hidden transition-all duration-200 max-w-0 group-hover:max-w-[120px] opacity-0 group-hover:opacity-100 whitespace-nowrap">
              Details
            </span>
          </button>

          <!-- URL Link: completed badge -->
          <button
            v-if="m.type === 'URL Link' && isCompleted(m)"
            disabled
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg cursor-not-allowed"
          >
            <CheckCircle class="h-3.5 w-3.5 shrink-0" />
            Completed
          </button>

          <!-- Doc File: completed badge -->
          <button
            v-if="m.type === 'Doc File' && isCompleted(m)"
            disabled
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg cursor-not-allowed"
          >
            <CheckCircle class="h-3.5 w-3.5 shrink-0" />
            Completed
          </button>

          <!-- Doc File / URL Link actions -->
          <template v-if="(m.type === 'Doc File' || m.type === 'URL Link') && !isCompleted(m)">
            <button
              v-if="m.file_path"
              @click="handlePrint(m)"
              class="group inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-300 transition-all"
              title="Print Document"
            >
              <Printer class="h-4 w-4 shrink-0 " />
              <span class="overflow-hidden transition-all duration-200 max-w-0 group-hover:max-w-[80px] opacity-0 group-hover:opacity-100 whitespace-nowrap">
                Print
              </span>
            </button>
            <button
              v-if="m.file_path"
              @click="handleDownload(m)"
              class="group inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-primary hover:bg-primary hover:text-white transition-all"
              title="Download Document"
            >
              <DownloadIcon class="h-4 w-4 shrink-0" />
              <span class="overflow-hidden transition-all duration-200 max-w-0 group-hover:max-w-[100px] opacity-0 group-hover:opacity-100 whitespace-nowrap">
                Download
              </span>
            </button>
            <button
              @click="openSubmitModal(m)"
              class="group inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all"
              title="Submit Completed Form"
            >
              <Send class="h-4 w-4 shrink-0" />
              <span class="overflow-hidden transition-all duration-200 max-w-0 group-hover:max-w-[80px] opacity-0 group-hover:opacity-100 whitespace-nowrap">
                Submit
              </span>
				</button>
			</template>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-12 text-slate-500">
      No materials found matching your criteria.
    </div>

    <div
      v-if="!loading && !error && filteredTotal > 0"
      class="card border border-gray-200/50 dark:border-gray-700/50 shadow-sm sticky bottom-0 z-30"
    >
      <div class="p-4">
        <PaginationBar
          v-if="!loading && !error && filteredTotal > 0"
          :current-page="currentPage"
          :total-pages="filteredTotalPages"
          :total="filteredTotal"
          :per-page="perPage"
          @update:page="goToPage"
        />
      </div>
    </div>

    <MaterialDetailsModal
      :is-open="showDetailsModal"
      :material="selectedMaterial"
      @close="showDetailsModal = false; selectedMaterial = null"
    />

    <!-- Submit Form Modal -->
    <Modal v-model="showSubmitModal" title="Submit Completed Form">
      <div class="space-y-4">
        <p class="text-sm text-slate-600 dark:text-slate-300">Please upload your completed form for <strong>{{ currentFormMaterial?.title }}</strong>.</p>
        <div
          class="rounded-2xl border-2 border-dashed p-6 cursor-pointer select-none bg-white/60 dark:bg-gray-800/50 border-gray-300/70 dark:border-gray-600/60 hover:bg-white/80 dark:hover:bg-gray-800/70"
          :class="isDragOver ? 'ring-2 ring-primary/50 border-primary/60' : ''"
          @click="fileInputRef?.click()"
          @dragenter.prevent="isDragOver = true"
          @dragover.prevent="isDragOver = true"
          @dragleave.prevent="isDragOver = false"
          @drop.prevent="onDrop"
        >
          <div class="flex items-center gap-4 w-full">
            <div class="h-11 w-11 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200">
              <Upload class="h-5 w-5" />
            </div>
            <div class="min-w-0 flex-1 overflow-hidden">
              <template v-if="selectedFiles.length > 0">
                <p class="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{{ selectedFiles.length }} file(s) selected</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">Click or drop to add more files</p>
              </template>
              <template v-else>
                <p class="text-sm font-semibold text-gray-800 dark:text-gray-100">Drag &amp; drop your files here</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">or click to browse</p>
              </template>
            </div>
          </div>
        </div>
        <div v-if="selectedFiles.length > 0" class="space-y-1">
          <div v-for="(file, i) in selectedFiles" :key="i" class="flex items-center justify-between text-sm text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-1.5">
            <span class="truncate">{{ file.name }}</span>
            <button @click="removeFile(i)" class="text-red-500 hover:text-red-700 ml-2 shrink-0">
              <X class="h-4 w-4" />
            </button>
          </div>
        </div>
        <input ref="fileInputRef" type="file" class="hidden" multiple @change="handleFileChange" />
      </div>
      <template #footer>
        <button type="button" class="px-4 py-2 border rounded-lg dark:border-slate-600" @click="showSubmitModal = false">Cancel</button>
        <button type="button" class="ml-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700" :disabled="selectedFiles.length === 0 || submitting" @click="submitForm">
          {{ submitting ? 'Submitting...' : 'Submit Form' }}
        </button>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { materialService } from '@/services/material.service'
import { completedFormService } from '@/services/completedFormService'
import type { Material } from '@/config/types/material'
import type { CompletedForm } from '@/services/completedFormService'
import Modal from '@/components/layout/Modal.vue'
import MaterialDetailsModal from '@/components/materials/MaterialDetailsModal.vue'
import TableControls from '@/components/layout/TableControls.vue'
import PaginationBar from '@/components/layout/PaginationBar.vue'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/stores/auth'
import TableLoader from '@/components/ui/TableLoader.vue'
import {
  Printer, Eye as ViewIcon,
  CloudDownload as DownloadIcon, Send, Info,
  FileText, Globe, Link2, CheckCircle, Upload, X
} from 'lucide-vue-next'

const toast = useToast()
const toastTimeout = 5000

const router = useRouter()
const authStore = useAuthStore()

const materials = ref<Material[]>([])
const completedForms = ref<CompletedForm[]>([])
const loading = ref(false)
const error = ref('')

const showDetailsModal = ref(false)
const selectedMaterial = ref<Material | null>(null)

const showSubmitModal = ref(false)
const currentFormMaterial = ref<Material | null>(null)
const selectedFiles = ref<File[]>([])
const isDragOver = ref(false)
const fileInputRef = ref<HTMLInputElement | null>(null)
const submitting = ref(false)

const searchQuery = ref('')
const statusFilter = ref('')
const typeFilter = ref('')

const statusOptions = [
  { label: 'All', value: '' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
]

const typeOptions = [
  { label: 'All Types', value: '' },
  { label: 'Document Files', value: 'Doc File' },
  { label: 'URL Links', value: 'URL Link' },
  { label: 'Online Forms', value: 'Online form' },
]

const userId = computed(() => authStore.currentUser?.id)
const positionId = computed(() => authStore.currentUser?.position_id)

const assignedMaterials = computed(() => {
  const uid = userId.value
  const pid = positionId.value
  if (!uid) return materials.value
  return materials.value.filter(m => {
    const posMatch = m.position_viewers?.some(p => p.id === pid)
    const userMatch = m.user_viewers?.some(u => u.id === uid)
    const noViewers = (!m.position_viewers || m.position_viewers.length === 0) &&
                      (!m.user_viewers || m.user_viewers.length === 0)
    return posMatch || userMatch || noViewers
  })
})

const completedMaterialIds = computed(() => {
  const uid = userId.value
  if (!uid) return new Set<number>()
  const ids = completedForms.value
    .filter(f => Number(f.user_id) === uid)
    .map(f => Number(f.fk_material_id ?? (f as any).material_id))
    .filter(Boolean)
  return new Set(ids)
})

const isCompleted = (m: Material) => {
  return completedMaterialIds.value.has(m.id)
}

const statusFiltered = computed(() => {
  const list = assignedMaterials.value
  if (!statusFilter.value) return list
  return list.filter(m => m.status === statusFilter.value)
})

const typeFiltered = computed(() => {
  const list = statusFiltered.value
  if (!typeFilter.value) return list
  return list.filter(m => m.type === typeFilter.value)
})

const searchFiltered = computed(() => {
  const list = typeFiltered.value
  if (!searchQuery.value.trim()) return list
  const query = searchQuery.value.toLowerCase()
  return list.filter(m => {
    const title = (m.title || '').toLowerCase()
    const description = (m.description || '').toLowerCase()
    return title.includes(query) || description.includes(query)
  })
})

const perPage = ref(12)
const currentPage = ref(1)
const filteredTotalPages = computed(() => Math.max(1, Math.ceil(searchFiltered.value.length / perPage.value)))
const filteredTotal = computed(() => searchFiltered.value.length)

const paginatedMaterials = computed(() => {
  const start = (currentPage.value - 1) * perPage.value
  const end = start + perPage.value
  return searchFiltered.value.slice(start, end)
})

watch([statusFilter, typeFilter, searchQuery, perPage], () => {
  currentPage.value = 1
})

const goToPage = (page: number) => {
  currentPage.value = page
}

const fetchData = async () => {
  loading.value = true
  error.value = ''
  try {
    const [materialsRes, formsRes] = await Promise.all([
      materialService.fetchAll({ per_page: 1000 }),
      completedFormService.fetchAll(),
    ])
    materials.value = Array.isArray(materialsRes) ? materialsRes : (Array.isArray(materialsRes.data) ? materialsRes.data : [])
    completedForms.value = formsRes
  } catch (err: any) {
    error.value = err.message || 'Failed to fetch data'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchData()
})

const handleView = (m: Material) => {
  if (m.type === 'Online form') {
    router.push({ name: 'employee-view-online-form', params: { id: m.id } })
  } else {
    selectedMaterial.value = m
    showDetailsModal.value = true
  }
}

const handlePrint = async (m: Material) => {
  if (!m.file_path) return
  const loadingToast = toast.info('Preparing document for printing...', { timeout: false })
  try {
    await materialService.printFile(m.id)
    toast.dismiss(loadingToast)
    toast.success('Document printed successfully', { timeout: toastTimeout })
  } catch (err: any) {
    toast.dismiss(loadingToast)
    toast.error(err.response?.data?.message || err.message || 'Failed to print file', { timeout: toastTimeout })
  }
}

const handleDownload = async (m: Material) => {
  const loadingToast = toast.info('Preparing document for download...', { timeout: false })
	try {
		const token = localStorage.getItem('token')
		const url = materialService.getDownloadUrl(m.id)
		const response = await fetch(url, {
			headers: token ? { Authorization: `Bearer ${token}` } : {},
		})
		if (!response.ok) throw new Error('Invalid File')
		const blob = await response.blob()
		const downloadUrl = window.URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = downloadUrl
		a.download = m.title || 'download'
		document.body.appendChild(a)
		a.click()
		window.URL.revokeObjectURL(downloadUrl)
		document.body.removeChild(a)
    toast.dismiss(loadingToast)
    toast.success('Document downloaded successfully', { timeout: toastTimeout })
	} catch (err: any) {
    toast.dismiss(loadingToast)
		toast.error(err.response?.data?.message || err.message || 'Failed to download file', { timeout: toastTimeout })
	}
}

const openSubmitModal = (m: Material) => {
  currentFormMaterial.value = m
  selectedFiles.value = []
  isDragOver.value = false
  showSubmitModal.value = true
}

const handleFileChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  if (target.files) {
    selectedFiles.value = [...selectedFiles.value, ...Array.from(target.files)]
  }
  target.value = ''
}

const onDrop = (e: DragEvent) => {
  isDragOver.value = false
  if (e.dataTransfer?.files) {
    selectedFiles.value = [...selectedFiles.value, ...Array.from(e.dataTransfer.files)]
  }
}

const removeFile = (index: number) => {
  selectedFiles.value.splice(index, 1)
}

const submitForm = async () => {
  if (!currentFormMaterial.value || selectedFiles.value.length === 0) return
  submitting.value = true
  try {
    const formData = new FormData()
    formData.append('material_id', String(currentFormMaterial.value.id))
    for (const file of selectedFiles.value) {
      formData.append('files[]', file)
    }
    const res = await completedFormService.create(formData) as any
    if (res && res.material_id) {
      completedForms.value.push({
        form_id: 0,
        fk_material_id: res.material_id,
        material_id: res.material_id,
        user_id: userId.value,
        status: 'submitted',
      } as any)
    }
    toast.success(`${selectedFiles.value.length} file(s) submitted successfully`, { timeout: toastTimeout })
    showSubmitModal.value = false
    selectedFiles.value = []
    currentFormMaterial.value = null
  } catch (err) {
    toast.error('Failed to submit form', { timeout: toastTimeout })
  } finally {
    submitting.value = false
  }
}
</script>
